(() => {
  'use strict';
  if (window.z12NativeReady) return;
  window.z12NativeReady = true;
  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const activeDetailVideos = new Set();
  const detailObserver = 'IntersectionObserver' in window ? new IntersectionObserver(entries => {
    entries.forEach(entry => { if (!entry.isIntersecting || entry.intersectionRatio <= .03) resetVideo(entry.target); });
  }, { threshold: [0, .03] }) : null;
  const resetVideo = (container) => {
    activeDetailVideos.delete(container); detailObserver?.unobserve(container);
    container.querySelectorAll('[data-z12n-loaded]').forEach((media) => {
      media.z12PlaybackCleanup?.();
      if (media.tagName === 'VIDEO') {
        media.pause();
        if (container.classList.contains('z12n-media--natural')) { media.removeAttribute('src'); media.querySelectorAll('source').forEach(source => source.removeAttribute('src')); media.load(); }
      }
      media.remove();
    });
    container.querySelectorAll('[data-z12n-video-poster], .z12n-video-poster, [data-z12n-video-play]').forEach((node) => { node.hidden = false; });
    container.classList.remove('is-playing');
    delete container.dataset.z12nVideoStarted;
  };
  document.addEventListener('click', (event) => {
    const play = event.target.closest('[data-z12n-video-play]');
    if (play) {
      const container = play.closest('[data-z12n-video]');
      const template = container?.querySelector('template[data-z12n-video-template]');
      if (!template || container.dataset.z12nVideoStarted === 'true') return;
      const fragment = template.content.cloneNode(true);
      const media = fragment.querySelector('video, iframe');
      if (!media) return;
      media.dataset.z12nLoaded = 'true';
      if (media.tagName === 'IFRAME') {
        const url = new URL(media.src, location.href);
        url.searchParams.set('autoplay', '1');
        if (container.classList.contains('z12n-media--natural') && window.z12PlayRequestedMedia) { media.dataset.videoSrc = url.toString(); media.removeAttribute('src'); }
        else media.src = url.toString();
        media.setAttribute('allow', 'autoplay; fullscreen; picture-in-picture; encrypted-media');
      }
      (container.querySelector('[data-z12n-video-mount]') || container).append(fragment);
      container.querySelectorAll('[data-z12n-video-poster], .z12n-video-poster').forEach((node) => { node.hidden = true; });
      play.hidden = true;
      container.dataset.z12nVideoStarted = 'true';
      container.classList.add('is-playing');
      if (container.classList.contains('z12n-media--natural')) { activeDetailVideos.add(container); detailObserver?.observe(container); }
      media.setAttribute('tabindex', '0');
      media.focus({ preventScroll: true });
      if (container.classList.contains('z12n-media--natural') && window.z12PlayRequestedMedia) window.z12PlayRequestedMedia(media, () => media.isConnected && container.dataset.z12nVideoStarted === 'true' && !document.hidden, () => resetVideo(container));
      else if (media.tagName === 'VIDEO') media.play().catch(() => { media.controls = true; });
      return;
    }
    const anchor = event.target.closest('a[href^="#z12-"]');
    if (!anchor || document.querySelector('[data-z12n-no-section-anchors]')) return;
    const target = document.getElementById(anchor.hash.slice(1));
    if (!target) return;
    event.preventDefault();
    target.scrollIntoView({ behavior: reducedMotion() ? 'auto' : 'smooth', block: 'start' });
    history.replaceState(null, '', anchor.hash);
  });
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) document.querySelectorAll('[data-z12n-video][data-z12n-video-started]').forEach(resetVideo);
  });
  document.addEventListener('shopify:section:unload', event => {
    [...activeDetailVideos].forEach(container => { if (event.target === container || event.target.contains(container)) resetVideo(container); });
  });
  const initProduct = (root) => {
    if (root.dataset.z12nReady === 'true') return;
    root.dataset.z12nReady = 'true';
    let variants;
    try { variants = JSON.parse(root.querySelector('[data-z12n-variants]').textContent); } catch (_) { return; }
    const form = root.querySelector('form');
    const input = root.querySelector('[data-z12n-variant]');
    const selectors = [...root.querySelectorAll('[data-z12n-option]')];
    const submit = root.querySelector('[data-z12n-submit]');
    const quantity = form?.elements.namedItem('quantity');
    const bar = root.querySelector('[data-z12n-buybar]');
    const barSubmit = root.querySelector('[data-z12n-bar-submit]');
    const payment = root.querySelector('[data-z12n-payment]');
    if (!form || !input || !submit) return;
    let currentVariant = variants.find((variant) => String(variant.id) === input.value);
    let purchasePassed = false;
    const setText = (selector, value) => {
      const node = root.querySelector(selector);
      if (node && node.textContent !== value) node.textContent = value;
    };
    const showMedia = (id) => {
      if (!id || !root.querySelector(`[data-z12n-media-id="${id}"]`)) return;
      root.querySelectorAll('[data-z12n-media-id]').forEach((slide) => {
        const active = slide.dataset.z12nMediaId === String(id);
        if (!active && !slide.hidden) slide.querySelectorAll('[data-z12n-video]').forEach(resetVideo);
        slide.hidden = !active;
      });
      root.querySelectorAll('[data-z12n-thumb]').forEach((button) => button.setAttribute('aria-pressed', String(button.dataset.z12nThumb === String(id))));
    };
    const syncBar = () => {
      if (!bar) return;
      const busy = submit.getAttribute('aria-disabled') === 'true' || submit.classList.contains('loading');
      if (barSubmit) {
        barSubmit.disabled = submit.disabled || busy;
        barSubmit.setAttribute('aria-busy', String(busy));
      }
      const blockers = document.querySelectorAll('cart-drawer.active, .drawer.active, dialog[open], #shopify-pc__banner, .cookies-banner:not(.hidden), [aria-modal="true"][open]');
      const blocked = [...blockers].some((node) => !node.hidden && node.getClientRects().length > 0 && getComputedStyle(node).visibility !== 'hidden') || document.body.classList.contains('overflow-hidden');
      bar.hidden = !purchasePassed || blocked;
    };
    const applyVariant = (variant, updateUrl) => {
      currentVariant = variant;
      const available = !!variant?.available;
      const label = variant ? (available ? root.dataset.addLabel : root.dataset.soldOutLabel) : root.dataset.unavailableLabel;
      input.value = variant ? String(variant.id) : '';
      submit.disabled = !available;
      submit.querySelector('span').textContent = label;
      if (barSubmit) barSubmit.textContent = label;
      if (payment) payment.hidden = !available;
      if (variant) {
        setText('[data-z12n-price]', variant.priceText);
        setText('[data-z12n-bar-price]', variant.priceText);
        setText('[data-z12n-compare]', variant.compareText);
        setText('[data-z12n-bar-configuration]', variant.title);
        const compare = root.querySelector('[data-z12n-compare]');
        if (compare) compare.hidden = root.dataset.hideCompare === 'true' || !(variant.compare_at_price > variant.price);
        selectors.forEach((select, index) => { select.value = variant.options[index]; });
        document.querySelectorAll('[data-z12n-configuration-link]').forEach((link) => {
          const href = variant.options[0] === '220V' ? (link.dataset.url220 || link.dataset.url110) : link.dataset.url110;
          if (!href) return;
          const destination = new URL(href, location.href);
          const previewId = new URL(location.href).searchParams.get('preview_theme_id');
          if (previewId && destination.origin === location.origin) destination.searchParams.set('preview_theme_id', previewId);
          link.href = destination.toString();
        });
        if (quantity) {
          const rule = variant.quantity_rule || {};
          quantity.min = String(rule.min || 1);
          quantity.step = String(rule.increment || 1);
          if (rule.max) quantity.max = String(rule.max); else quantity.removeAttribute('max');
        }
        if (updateUrl) {
          const url = new URL(location.href);
          url.searchParams.set('variant', String(variant.id));
          history.replaceState(null, '', url.toString());
          showMedia(variant.mediaId);
        }
      } else {
        setText('[data-z12n-price]', root.dataset.unavailableLabel);
        setText('[data-z12n-bar-price]', root.dataset.unavailableLabel);
      }
      if (updateUrl) {
        input.dispatchEvent(new Event('change', { bubbles: true }));
        document.dispatchEvent(new CustomEvent('afterVariantChanged', { detail: { sectionId: root.dataset.sectionId, variant } }));
      }
      syncBar();
    };
    selectors.forEach((select) => select.addEventListener('change', () => {
      const values = selectors.map((item) => item.value);
      applyVariant(variants.find((variant) => variant.options.every((value, index) => value === values[index])), true);
    }));
    root.querySelector('[data-z12n-options]').hidden = false;
    root.querySelector('[data-z12n-variant-fallback]').hidden = true;
    root.querySelectorAll('[data-z12n-thumb]').forEach((button) => button.addEventListener('click', () => showMedia(button.dataset.z12nThumb)));
    barSubmit?.addEventListener('click', () => {
      if (!currentVariant?.available || submit.disabled || submit.getAttribute('aria-disabled') === 'true') return;
      if (!form.reportValidity()) {
        form.scrollIntoView({ block: 'center', behavior: reducedMotion() ? 'auto' : 'smooth' });
        return;
      }
      form.requestSubmit(submit);
    });
    const buttonObserver = new MutationObserver(syncBar);
    buttonObserver.observe(submit, { attributes: true, attributeFilter: ['disabled', 'aria-disabled', 'class'] });
    const bodyObserver = new MutationObserver(syncBar);
    bodyObserver.observe(document.body, { attributes: true, attributeFilter: ['class'], childList: true });
    const drawer = document.querySelector('cart-drawer');
    if (drawer) bodyObserver.observe(drawer, { attributes: true, attributeFilter: ['class'] });
    document.querySelectorAll('#shopify-pc__banner, .cookies-banner').forEach((banner) => bodyObserver.observe(banner, { attributes: true, attributeFilter: ['class', 'style', 'hidden'] }));
    let visibilityObserver;
    if ('IntersectionObserver' in window) {
      visibilityObserver = new IntersectionObserver((entries) => {
        purchasePassed = entries[0].boundingClientRect.bottom < 0 && !entries[0].isIntersecting;
        syncBar();
      }, { threshold: 0 });
      visibilityObserver.observe(form);
    }
    root.z12nCleanup = () => { buttonObserver.disconnect(); bodyObserver.disconnect(); visibilityObserver?.disconnect(); };
    applyVariant(currentVariant, false);
  };
  const init = (scope = document) => {
    const previewId = new URL(location.href).searchParams.get('preview_theme_id');
    if (previewId) scope.querySelectorAll('a[href*="view=z12-native"]').forEach((link) => {
      const url = new URL(link.href, location.href);
      if (url.origin === location.origin) { url.searchParams.set('preview_theme_id', previewId); link.href = url.toString(); }
    });
    if (scope.matches?.('[data-z12n-product]')) initProduct(scope);
    scope.querySelectorAll('[data-z12n-product]').forEach(initProduct);
  };
  document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', () => init(), { once: true }) : init();
  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => event.target.querySelectorAll('[data-z12n-product]').forEach((root) => root.z12nCleanup?.()));
})();
